// CompanyPill (DESIGN §3.1.5): Pill 40 auf `card` mit Icon-Kreis 28 und Firmenname.
// Mit `entities` öffnet sie ein Popover mit den Gesellschaften (Admin-Topbar); sonst nur Anzeige.
import { Buildings, CaretDown } from '@phosphor-icons/react';
import { Popover } from 'radix-ui';
import { cn } from '../kern/cn';
import { Link } from '../kern/Link';

export interface CompanyEntity {
  name: string;
  country: string;
  currency: string;
  fiscalYear?: string;
}

export interface CompanyPillProps {
  name: string;
  entities?: readonly CompanyEntity[];
  /** Link „Company settings“ (nur ADM). */
  settingsHref?: string;
  className?: string;
}

const pillClass =
  'inline-flex h-10 max-w-60 min-w-0 shrink-0 items-center gap-2 rounded-full bg-card pr-3 pl-1.5 text-button text-fg';

function PillBody({ name, withChevron }: { name: string; withChevron?: boolean }) {
  return (
    <>
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-tile">
        <Buildings size={16} aria-hidden />
      </span>
      <span className="min-w-0 truncate">{name}</span>
      {withChevron ? (
        <CaretDown size={16} aria-hidden className="shrink-0 text-fg-tertiary" />
      ) : null}
    </>
  );
}

export function CompanyPill({ name, entities, settingsHref, className }: CompanyPillProps) {
  if (!entities || entities.length === 0) {
    return (
      <div role="img" aria-label={`Company: ${name}`} className={cn(pillClass, className)}>
        <PillBody name={name} />
      </div>
    );
  }
  return (
    <Popover.Root>
      <Popover.Trigger
        aria-label={`Company: ${name}`}
        className={cn(
          pillClass,
          'transition-colors duration-(--bar-dur-fast) hover:bg-card-hover',
          className,
        )}
      >
        <PillBody name={name} withChevron />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          collisionPadding={8}
          className="z-(--bar-z-popover) w-80 rounded-xl bg-overlay p-2 shadow-2 data-[state=open]:animate-pop-in data-[state=closed]:animate-pop-out"
        >
          <p className="px-3 pt-2 pb-1 text-chip text-fg-tertiary">Legal entities</p>
          <ul>
            {entities.map((entity) => (
              <li
                key={entity.name}
                className="flex min-h-12 items-center justify-between gap-3 rounded-lg px-3 py-2"
              >
                <span className="min-w-0">
                  <span className="block truncate text-body text-fg">{entity.name}</span>
                  <span className="block truncate text-label text-fg-secondary">
                    {entity.country} · {entity.currency}
                    {entity.fiscalYear ? ` · FY ${entity.fiscalYear}` : ''}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          {settingsHref ? (
            <div className="border-t border-line-subtle px-3 pt-2 pb-1">
              <Link href={settingsHref} className="text-label">
                Company settings
              </Link>
            </div>
          ) : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
